import { z } from 'zod';
import type { ListPublicEventsInput } from '@/lib/queries/events';
import {
  PUBLIC_EVENT_DATE_PRESETS,
  datePresetRange,
  isValidTimeZone,
  type PublicEventDatePreset,
} from './public-event-time';

export type PublicEventsDate = PublicEventDatePreset | 'any';
/** The signed-in list's `format` vocabulary, shared so one `?format=` link
 *  means the same on both pages; `virtual` asks the API for `online`. */
export type PublicEventsFormat = 'any' | 'in_person' | 'virtual';

// Each param is parsed on its own (`catch`): one stale or hand-edited value
// falls back to its default without discarding the rest of the URL. `sort`
// is accepted and discarded: the list has one fixed order (soonest first),
// but an old `?sort=latest` link must still open the page instead of
// falling through `catch` on the whole object.
export const publicEventsFilterSchema = z
  .object({
    q: z.string().optional().catch(undefined),
    date: z.enum(PUBLIC_EVENT_DATE_PRESETS).optional().catch(undefined),
    format: z.enum(['in_person', 'virtual']).optional().catch(undefined),
    country: z.string().regex(/^[A-Z]{2}$/).optional().catch(undefined),
    topic: z.string().min(1).max(40).optional().catch(undefined),
    sort: z.enum(['soonest', 'latest']).optional().catch(undefined),
    tz: z.string().refine(isValidTimeZone).optional().catch(undefined),
  })
  .transform((v) => ({
    q: v.q ?? '',
    date: (v.date ?? 'any') as PublicEventsDate,
    format: (v.format ?? 'any') as PublicEventsFormat,
    country: v.country ?? '',
    topic: v.topic ?? '',
    // Empty = the viewer's own zone, resolved at render.
    tz: v.tz ?? '',
  }));

export type PublicEventsFilters = z.infer<typeof publicEventsFilterSchema>;

export const PUBLIC_EVENTS_FILTER_DEFAULTS: PublicEventsFilters = {
  q: '',
  date: 'any',
  format: 'any',
  country: '',
  topic: '',
  tz: '',
};

/** The public params as the route's `validateSearch` keeps them: only the
 *  present, valid ones, so a default never lands in the URL. */
export type PublicEventsSearchInput = {
  date?: PublicEventDatePreset;
  country?: string;
  topic?: string;
  tz?: string;
};

export function parsePublicEventsSearch(raw: Record<string, unknown>): PublicEventsSearchInput {
  const v = publicEventsFilterSchema.parse(raw);
  return {
    ...(v.date !== 'any' ? { date: v.date } : {}),
    ...(v.country !== '' ? { country: v.country } : {}),
    ...(v.topic !== '' ? { topic: v.topic } : {}),
    ...(v.tz !== '' ? { tz: v.tz } : {}),
  };
}

/** Whether the reader has narrowed the list — search, any filter. Time zone
 *  only relabels the same events, so it doesn't count. */
export function hasActivePublicEventsFilters(v: PublicEventsFilters): boolean {
  return (
    v.q.trim().length > 0 ||
    v.date !== 'any' ||
    v.format !== 'any' ||
    v.country !== '' ||
    v.topic !== ''
  );
}

/** Clears search and every filter, keeping the reader's zone. */
export function clearedPublicEventsFilters(v: PublicEventsFilters): PublicEventsFilters {
  return { ...PUBLIC_EVENTS_FILTER_DEFAULTS, tz: v.tz };
}

/** The API input for the filters. `now` and `timeZone` place the date
 *  preset's calendar days (see `datePresetRange`). The list has one fixed
 *  order (soonest first), so no `sort` is sent. */
export function publicEventsListInput(
  v: PublicEventsFilters,
  { now, timeZone }: { now: Date; timeZone: string },
): Omit<ListPublicEventsInput, 'cursor' | 'pageSize'> {
  const q = v.q.trim();
  return {
    ...(q.length > 0 ? { q } : {}),
    ...(v.date !== 'any' ? datePresetRange(v.date, now, timeZone) : {}),
    ...(v.format === 'virtual' ? { format: 'online' as const } : {}),
    ...(v.format === 'in_person' ? { format: 'in_person' as const } : {}),
    ...(v.country !== '' ? { countryCode: v.country } : {}),
    ...(v.topic !== '' ? { topic: v.topic } : {}),
  };
}
